import { dimensionBadgeBase } from '@src/components/common/dimensionBadgeStyles';
import DurationOverlayBadge from '@src/components/common/DurationOverlayBadge';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

// Inline SVG icons
const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const VideoIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m22 8-6 4 6 4V8Z" />
    <rect width="14" height="12" x="2" y="6" rx="2" ry="2" />
  </svg>
);

const FileTextIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

export interface StoryDimension {
  aspectRatio: string;
  /** True when at least one clip in this dimension has a rendered file. */
  isPublished: boolean;
  /** Number of the story's clips that offer this dimension. */
  clipCount: number;
}

export interface HomeStoryCardProps {
  id: string;
  title: string;
  thumbnail?: string;
  duration?: string;
  updatedBy?: string;
  updatedDate?: string;
  clips?: number;
  articles?: number;
  /** Aspect ratios available across the story's clips. */
  dimensions?: StoryDimension[];
  /**
   * Short status shown over the thumbnail while the upload pipeline is still
   * running (PLAT-11224 placeholder card variant); omit once fully ready.
   */
  processingLabel?: string;
  /** Open the clip preview on this aspect ratio in Content Studio. */
  onDimensionClick?: (aspectRatio: string) => void;
  onClick?: () => void;
}

export const HomeStoryCard = observer(
  ({
    id,
    title,
    thumbnail,
    duration,
    updatedBy,
    updatedDate,
    clips = 0,
    articles = 0,
    dimensions = [],
    processingLabel,
    onDimensionClick,
    onClick,
  }: HomeStoryCardProps) => {
    const [imageError, setImageError] = useState(false);

    return (
      <Card
        onClick={onClick}
        $clickable={!!onClick}
        data-testid={`home-story-card-${id}`}
      >
        <ThumbnailContainer>
          {thumbnail && !imageError ? (
            <Thumbnail
              src={thumbnail}
              alt={title}
              onError={() => setImageError(true)}
            />
          ) : (
            <NoThumbnail>No thumbnail</NoThumbnail>
          )}
          <HoverOverlay className="hover-overlay">
            <SeeDetailsButton
              onClick={e => {
                e.stopPropagation();
                onClick?.();
              }}
            >
              <span>See details</span>
              <ArrowRightIcon />
            </SeeDetailsButton>
          </HoverOverlay>
          {duration && <DurationOverlayBadge>{duration}</DurationOverlayBadge>}
          {processingLabel && (
            <ProcessingOverlayBadge>{processingLabel}</ProcessingOverlayBadge>
          )}
        </ThumbnailContainer>

        <InfoSection>
          <TitleArea>
            <Title>{title}</Title>
            {updatedBy && <UpdatedBy>Updated by {updatedBy}</UpdatedBy>}
            {updatedDate && <UpdatedDate>Updated {updatedDate}</UpdatedDate>}
          </TitleArea>

          <StatsRow>
            <StatItem $isZero={clips === 0}>
              <VideoIcon />
              <span>{clips} clips</span>
            </StatItem>
            <StatItem $isZero={articles === 0}>
              <FileTextIcon />
              <span>{articles} articles</span>
            </StatItem>
          </StatsRow>

          {dimensions.length > 0 && (
            <DimensionsRow>
              {dimensions.map(format => (
                <DimensionBadge
                  key={format.aspectRatio}
                  $published={format.isPublished}
                  $clickable={!!onDimensionClick}
                  data-tooltip-id={`story-dimensions-${id}`}
                  data-tooltip-content={[
                    format.isPublished ? 'Published' : 'Draft',
                    // Flag when several clips share the ratio — the badge
                    // opens the most recently updated one.
                    format.clipCount > 1 ? `${format.clipCount} Clips` : null,
                    `Open ${format.aspectRatio} Preview`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  onClick={
                    onDimensionClick
                      ? e => {
                          // Keep the card's own click (plain story navigation)
                          // from also firing.
                          e.stopPropagation();
                          onDimensionClick(format.aspectRatio);
                        }
                      : undefined
                  }
                >
                  {format.aspectRatio}
                </DimensionBadge>
              ))}
              <Tooltip
                id={`story-dimensions-${id}`}
                place="bottom"
                noArrow
                positionStrategy="fixed"
                style={COMPACT_TOOLTIP_STYLE}
              />
            </DimensionsRow>
          )}
        </InfoSection>
      </Card>
    );
  },
);

const Card = styled.div<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background-color: var(--surface);
  border-radius: 8px;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: box-shadow var(--transition-normal) ease;

  &:hover {
    box-shadow: var(--elevation-sm);
  }

  &:hover .hover-overlay {
    opacity: 1;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background-color: var(--gray-500);
  overflow: hidden;
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  background-color: var(--background);
`;

const NoThumbnail = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border);
  font-size: var(--text-xs);
`;

/* Mirrors DurationOverlayBadge but anchored top-left so both can coexist. */
const ProcessingOverlayBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: white;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal) ease;
`;

const SeeDetailsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  flex: 1;
`;

const TitleArea = styled.div`
  margin-bottom: 12px;
`;

const Title = styled.h4`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  margin: 0 0 4px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const UpdatedBy = styled.p`
  font-size: 11px;
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
`;

const UpdatedDate = styled.p`
  font-size: 11px;
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: auto;
`;

const StatItem = styled.div<{ $isZero?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: var(--font-weight-regular);
  color: ${props => (props.$isZero ? 'var(--border)' : 'var(--text-muted)')};

  svg {
    color: ${props => (props.$isZero ? 'var(--border)' : 'var(--text-muted)')};
  }
`;

const DimensionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

/* Aspect-ratio badge: green filled when the dimension has a rendered/published
   file, outlined muted while it's still a draft. Sizing matches the
   DimensionBadge on Content Studio clip cards (VideoClipCard). */
const DimensionBadge = styled.div<{
  $published: boolean;
  $clickable?: boolean;
}>`
  ${dimensionBadgeBase}
  display: flex;
  box-sizing: border-box;
  font-weight: ${props => (props.$published ? 600 : 500)};
  transition: var(--transition-fast);
  border-color: ${props =>
    props.$published ? 'var(--primary)' : 'var(--border)'};
  color: ${props =>
    props.$published ? 'var(--background)' : 'var(--text-muted)'};
  background-color: ${props =>
    props.$published ? 'var(--primary)' : 'transparent'};

  ${props =>
    props.$clickable &&
    (props.$published
      ? `cursor: pointer;
         &:hover {
           background-color: var(--primary-hover);
           border-color: var(--primary-hover);
         }`
      : `cursor: pointer;
         &:hover {
           color: var(--foreground);
           border-color: var(--text-muted);
         }`)}
`;
